import { LTCaseUserOption } from "../../enum/LTCaseUserOption";
import {
  buildTree,
  calculateCaseInteriorFromHeightsWidths,
  calculateTree,
} from "../../genetic_optimize/ContentTree";
import type { CaseInteriorOptions } from "../../interfaces/AccessoryCompartmentOptions";
import type { CaseCompartment } from "../../interfaces/CaseCompartment";
import type { ContentItem } from "../../interfaces/ContentItem";
import {
  type PanelColor,
  PanelMaterialCategory,
} from "../../interfaces/PanelColor";
import type { ProfileType } from "../../interfaces/ProfileType";
import type { Root } from "../../interfaces/Root";
import type { TrayMaterialType } from "../../interfaces/TrayMaterialType";
import { OptionDBInstance } from "../../user_options/options_db";

// Constants for clean cutting and milling
export const CLEANCUT = 100; // make cutouts larger by this for clean cutting
export const MILLING_EXTRA = 3; // make cutouts larger by half milling

// Hilfsfunktionen für konsistente Defaults
/**
 * Returns a hard-coded light brown color for interior panels
 * Color:hsl(34, 75.20%, 26.90%) (a light brown, lighter than tan)
 */
export function getDefaultPanelColor(): PanelColor {
  return {
    name: "Hellbraun (Default)",
    description: "Hellbraun für Innenplatten",
    color: "#784b11", // light brown color
    boardSize: [2500, 1250],
    materialCategory: PanelMaterialCategory.BirchMultiplex,
    codesByThickness: { "7": "DEFAULT", "9": "DEFAULT" },
    kind: LTCaseUserOption.PanelColor,
    deleted: false,
  };
}

export function ensureStrength(
  strength: number | undefined,
  material?: TrayMaterialType,
): number {
  // Verwende strength falls vorhanden (auch wenn 0 - kann valide sein)
  if (strength !== undefined) {
    return strength;
  }
  // Sonst verwende material.strength falls vorhanden
  if (material?.strength !== undefined) {
    return material.strength;
  }
  // Nur als letzter Ausweg: Default 5mm
  return 5;
}

export function calculateProfileOffset(
  profiles: ProfileType,
  upperPaddingAlignment: boolean,
  overHead = false,
): number {
  if (!upperPaddingAlignment) {
    return -profiles.height;
  }
  // Original logic - check if name comparison is robust enough
  const isSTW1384 = (profiles as { name?: string }).name === "STW1384";
  if ((profiles.inverted !== overHead) !== isSTW1384) {
    return profiles.strength;
  }
  return profiles.extra - profiles.strength;
}

export function getVector3Index(axis: "x" | "y" | "z"): number {
  switch (axis) {
    case "x":
      return 0;
    case "y":
      return 1;
    case "z":
      return 2;
    default:
      return 0;
  }
}

export function getTrayMaterialWithFallback(
  trayMaterial: TrayMaterialType | undefined,
  buildstepName: string,
): TrayMaterialType | null {
  if (trayMaterial) return trayMaterial;

  console.warn(
    `[${buildstepName}] Missing trayMaterial in settings for CASE interior. Using default.`,
  );
  try {
    const defaultMaterial = OptionDBInstance.getOrThrow(
      LTCaseUserOption.TrayMaterialType,
      { name: "M1414" },
    ) as TrayMaterialType;
    return defaultMaterial;
  } catch {
    console.error(
      `[${buildstepName}] Could not load default trayMaterial. Aborting.`,
    );
    return null;
  }
}

export function getToleranceWithFallback(
  tolerance: { x: number; y: number; z: number } | undefined,
  buildstepName: string,
): { x: number; y: number; z: number } {
  if (tolerance) return tolerance;

  console.warn(
    `[${buildstepName}] Missing tolerance in settings for CASE interior. Using default.`,
  );
  return { x: 7, y: 2, z: 7 };
}

export function calculateFromTree(
  tree: Root,
  trayMaterial: TrayMaterialType,
  tolerance: { x: number; y: number; z: number },
  _buildstepName: string,
): { panels: unknown[]; volumes: CaseCompartment[]; mode: string } {
  const result = calculateTree(tree, trayMaterial.strength, tolerance);
  return { panels: result.panels, volumes: result.volumes, mode: "TREE" };
}

export function calculateFromContents(
  contents: ContentItem[],
  trayMaterial: TrayMaterialType,
  tolerance: { x: number; y: number; z: number },
  _buildstepName: string,
): { panels: unknown[]; volumes: CaseCompartment[]; mode: string } {
  const convertedTree = buildTree(contents);
  const result = calculateTree(convertedTree, trayMaterial.strength, tolerance);
  return { panels: result.panels, volumes: result.volumes, mode: "CONTENTS" };
}

export function calculateFromHeightsWidths(
  heights: number[],
  widths: number[][],
  depth: number,
  trayMaterial: TrayMaterialType,
  tolerance: { x: number; y: number; z: number },
  _buildstepName: string,
): { panels: unknown[]; volumes: CaseCompartment[]; mode: string } {
  const result = calculateCaseInteriorFromHeightsWidths(
    heights,
    widths,
    depth,
    trayMaterial.strength,
    tolerance,
  );
  return {
    panels: result.panels,
    volumes: result.volumes,
    mode: "HEIGHTS_WIDTHS",
  };
}

export function calculatePanelsAndVolumes(
  interior: CaseInteriorOptions,
  trayMaterial: TrayMaterialType,
  tolerance: { x: number; y: number; z: number },
  buildstepName: string,
): { panels: unknown[]; volumes: CaseCompartment[]; mode: string } | null {
  const { tree, contents, heights, widths, depth } = interior;

  if (tree) {
    return calculateFromTree(tree, trayMaterial, tolerance, buildstepName);
  }
  if (contents && contents.length > 0) {
    return calculateFromContents(
      contents,
      trayMaterial,
      tolerance,
      buildstepName,
    );
  }
  if (heights && widths && depth) {
    return calculateFromHeightsWidths(
      heights,
      widths,
      depth,
      trayMaterial,
      tolerance,
      buildstepName,
    );
  }
  return null;
}
